import { useEffect, useRef, useState, useCallback } from "react";
import { createRoot } from "react-dom/client";
import styles from "../MapMonitoring.module.css";
import {
    PointColor,
    CustomMark,
    Route,
    LayersConfig,
    RestrictionZone,
    MapLayerType,
    DrawnShapeType,
} from "../types";
import { MAP_CENTER, MAP_ZOOM } from "../constants";
import { config } from "../../../config";
import { ReactComponent as WarningIcon } from "../../../assets/icons/Warning.svg";
import { RouteService } from "../services/routeService";
import { LayerService } from "../services/layerService";
import { mapStringToCoordinate } from "../../../utils/mapCoordinate";

declare global {
    interface Window {
        ymaps?: any;
        __ymapsLoaded?: boolean;
        __ymapsLoading?: boolean;
    }
}

interface UseYandexMapProps {
    customMarks: CustomMark[];
    currentColors: PointColor[];
    criticalActive: boolean;
    routes?: Route[];
    activeRouteIds?: string[];
    layersConfig?: LayersConfig;
    visibleVehicles?: Array<{ id: string; coords: [number, number]; name?: string; hintContent?: string }>;
    visibleZones?: RestrictionZone[];
    containerPlatforms?: Array<{ id: string; name: string; centerPoint: string }>;
    onContextMenu: (x: number, y: number, coords: [number, number]) => void;
    searchCity?: string;
    onRequestShapeMeta?: (payload: {
        type: DrawnShapeType;
        coordinates: Array<[number, number]>;
    }) => Promise<{ name: string; description: string } | null>;
    onGeofenceCreated?: () => void;
    setOpenContainerPlatforms?: any;
    setOpenGeofenceModal?: (v: { isOpen: boolean; id: string }) => void;
    onFinishEditContour?: () => void;
}

type LayerMap = Record<MapLayerType, any>;

function getZIndex(
    layersConfig: LayersConfig | undefined,
    layer: MapLayerType,
    fallback: number
): number {
    return layersConfig?.[layer]?.zIndex ?? fallback;
}

function getCarIconSvg(color: string = "#1e98ff"): string {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <text x="16" y="20" text-anchor="middle" fill="white" font-size="22" font-weight="bold">🚗</text>
        </svg>
    `)}`;
}

function applyLayerOrderToMap(map: any, layers: LayerMap, layersConfig?: LayersConfig) {
    if (!map || !layers) return;

    (Object.keys(layers) as MapLayerType[]).forEach((k) => {
        try {
            map.geoObjects.remove(layers[k]);
        } catch {}
    });

    const ordered: MapLayerType[] = layersConfig
        ? LayerService.getLayersOrder(layersConfig)
        : (Object.keys(layers) as MapLayerType[]);

    const added = new Set<MapLayerType>();

    ordered.forEach((type) => {
        const layer = layers[type];
        if (layer) {
            map.geoObjects.add(layer);
            added.add(type);
        }
    });

    (Object.keys(layers) as MapLayerType[]).forEach((type) => {
        if (added.has(type)) return;
        map.geoObjects.add(layers[type]);
    });
}

function getCoordinatesSequenceFromCoords(
    type: DrawnShapeType,
    coords: any
): Array<[number, number]> {
    const outer = Array.isArray(coords) ? coords[0] : null;
    return Array.isArray(outer) ? outer : [];
}

// function hashStringToPositiveInt(value: string): number {
//     let hash = 0;
//     for (let i = 0; i < value.length; i++) {
//         hash = (hash * 31 + value.charCodeAt(i)) | 0;
//     }
//     return hash >>> 0;
// }

// function getColorByStableId(id: string, palette: PointColor[]): PointColor {
//     if (!palette || palette.length === 0) return "green";
//     const idx = hashStringToPositiveInt(id) % palette.length;
//     return palette[idx] ?? "green";
// }

export function useYandexMap({
                                 customMarks,
                                 currentColors,
                                 criticalActive,
                                 routes = [],
                                 activeRouteIds = [],
                                 layersConfig,
                                 visibleVehicles = [],
                                 visibleZones = [],
                                 containerPlatforms = [],
                                 onContextMenu,
                                 searchCity = "",
                                 onRequestShapeMeta,
                                 onGeofenceCreated,
                                 setOpenContainerPlatforms,
                                 setOpenGeofenceModal,
                                 onFinishEditContour,
                             }: UseYandexMapProps) {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const mapInstanceRef = useRef<any>(null);
    const layersRef = useRef<LayerMap | null>(null);
    const warningLayoutRef = useRef<any>(null);
    const tsPlacemarkByIdRef = useRef<Map<string, any>>(new Map());

    // Состояние только для режима рисования НОВЫХ полигонов
    const [drawMode, setDrawMode] = useState<"idle" | "drawing">("idle");
    const [activeDrawType, setActiveDrawType] = useState<DrawnShapeType | null>(null);
    const activeShapeObjRef = useRef<any>(null);
    const geofencePolygonByIdRef = useRef<Map<string, any>>(new Map());
    const activeEditObjRef = useRef<any>(null);
    const cityBoundsRef = useRef<any>(null);
    const searchPlacemarkRef = useRef<any>(null);
    const routeCacheRef = useRef(new Map<string, { obj: any; signature: string }>());
    const centeredOnceRef = useRef(false);
    function getCoordinatesSequenceFromPolygon(poly: any): Array<[number, number]> {
        const coords = poly?.geometry?.getCoordinates?.();
        const outer = Array.isArray(coords) ? coords[0] : null;
        return Array.isArray(outer) ? outer : [];
    }

    const [apiLoaded, setApiLoaded] = useState(false);
    const [mapReady, setMapReady] = useState(false);

    // ============================
    // 0) Загрузка ymaps
    // ============================
    useEffect(() => {
        if (window.__ymapsLoaded) {
            setApiLoaded(true);
            return;
        }
        if (window.__ymapsLoading) return;

        const existing = document.getElementById("ymaps-script") as HTMLScriptElement | null;
        if (existing) {
            window.__ymapsLoading = true;
            const onLoad = () => {
                window.__ymapsLoaded = true;
                window.__ymapsLoading = false;
                setApiLoaded(true);
            };
            existing.addEventListener("load", onLoad, { once: true });
            return;
        }

        window.__ymapsLoading = true;
        const script = document.createElement("script");
        script.id = "ymaps-script";
        script.src = `https://api-maps.yandex.ru/2.1/?apikey=${config.yandexMapsApiKey}&lang=ru_RU`;
        script.async = true;

        script.onload = () => {
            window.__ymapsLoaded = true;
            window.__ymapsLoading = false;
            setApiLoaded(true);
        };

        script.onerror = () => {
            window.__ymapsLoading = false;
            console.error("Не удалось загрузить Yandex Maps API");
        };

        document.body.appendChild(script);
    }, []);

    // ============================
    // 1) Лэйаут иконки warning
    // ============================
    const initWarningLayout = useCallback(() => {
        if (warningLayoutRef.current || !window.ymaps) return;

        const layout = window.ymaps.templateLayoutFactory.createClass(
            `<div class="${styles.warningIcon}"></div>`,
            {
                build: function () {
                    (layout as any).superclass.build.call(this);
                    const container = this.getParentElement()?.querySelector?.(`.${styles.warningIcon}`);
                    if (!container) return;
                    if ((container as any).__mounted) return;
                    (container as any).__mounted = true;
                    const root = createRoot(container);
                    root.render(<WarningIcon width={32} height={32} />);
                },
            }
        );

        warningLayoutRef.current = layout;
    }, []);

    // ============================
    // 2) Инициализация карты
    // ============================
    useEffect(() => {
        const currentRouteCacheRef = routeCacheRef.current
        const currentTsPlacemarkByIdRef = tsPlacemarkByIdRef.current
        if (!apiLoaded || mapInstanceRef.current || !mapRef.current) return;
        if (!window.ymaps) return;

        window.ymaps.ready(() => {
            const map = new window.ymaps.Map(mapRef.current!, {
                center: MAP_CENTER,
                zoom: MAP_ZOOM,
                controls: ["zoomControl"],
            });

            map.events.add("contextmenu", (e: any) => {
                const evt = e.get("domEvent")?.originalEvent;
                if (!evt) return;
                onContextMenu(evt.pageX, evt.pageY, e.get("coords"));
            });

            const layers: LayerMap = {
                basePoints: new window.ymaps.GeoObjectCollection(),
                tsPoints: new window.ymaps.GeoObjectCollection(),
                customMarks: new window.ymaps.GeoObjectCollection(),
                drawnShapes: new window.ymaps.GeoObjectCollection(),
                restrictedZones: new window.ymaps.GeoObjectCollection(),
                routes: new window.ymaps.GeoObjectCollection(),
                routePoints: new window.ymaps.GeoObjectCollection(),
                routeLabels: new window.ymaps.GeoObjectCollection(),
            };

            (Object.keys(layers) as MapLayerType[]).forEach((k) => map.geoObjects.add(layers[k]));
            applyLayerOrderToMap(map, layers, layersConfig);

            layersRef.current = layers;
            mapInstanceRef.current = map;
            setMapReady(true);
            const HIDE_ZOOM = 9.5;

            map.events.add("boundschange", () => {
                const zoom = map.getZoom();
                const visible = zoom > HIDE_ZOOM;

                const layers = layersRef.current;
                if (!layers) return;

                Object.values(layers).forEach(layer => {
                    layer.options.set("visible", visible);
                });
            });

            {
                const zoom = map.getZoom();
                const visible = zoom > HIDE_ZOOM;

                Object.values(layers).forEach(layer => {
                    layer.options.set("visible", visible);
                });
            }
        });

        return () => {
            try {
                if (searchPlacemarkRef.current && mapInstanceRef.current) {
                    mapInstanceRef.current.geoObjects.remove(searchPlacemarkRef.current);
                }
            } catch {}

            searchPlacemarkRef.current = null;
            cityBoundsRef.current = null;
            currentRouteCacheRef.clear();
            centeredOnceRef.current = false;
            currentTsPlacemarkByIdRef.clear();
            mapInstanceRef.current?.destroy?.();
            mapInstanceRef.current = null;
            layersRef.current = null;
            setMapReady(false);
        };
    }, [apiLoaded, layersConfig, onContextMenu]);

    useEffect(() => {
        if (!mapReady || !mapInstanceRef.current || !layersRef.current) return;
        applyLayerOrderToMap(mapInstanceRef.current, layersRef.current, layersConfig);
    }, [mapReady, layersConfig]);

    // ============================
    // 3) Поиск по адресу
    // ============================
    const initCityBounds = useCallback(async () => {
        if (!window.ymaps || !mapInstanceRef.current) return;
        const city = searchCity.trim();
        if (!city) {
            cityBoundsRef.current = null;
            return;
        }

        try {
            const res = await window.ymaps.geocode(city, { results: 1 });
            const first = res.geoObjects.get(0);
            if (!first) {
                cityBoundsRef.current = null;
                return;
            }
            const bounds = first.properties.get("boundedBy")?.bounds;
            cityBoundsRef.current = bounds ?? null;
        } catch (e) {
            cityBoundsRef.current = null;
            console.error("Не удалось получить границы города:", e);
        }
    }, [searchCity]);

    useEffect(() => {
        if (!mapReady) return;
        initCityBounds();
    }, [mapReady, initCityBounds]);

    const searchByAddress = useCallback(
        async (query: string) => {
            if (!window.ymaps || !mapInstanceRef.current) return;
            const q = query.trim();
            if (!q) return;

            const city = searchCity.trim();
            const composedQuery = city ? `${city}, ${q}` : q;

            try {
                const options: any = { results: 1 };
                if (cityBoundsRef.current) {
                    options.boundedBy = cityBoundsRef.current;
                    options.strictBounds = true;
                }

                const res = await window.ymaps.geocode(composedQuery, options);
                const first = res.geoObjects.get(0);
                if (!first) return;

                const coords = first.geometry.getCoordinates();
                mapInstanceRef.current.setCenter(coords, 16, { checkZoomRange: true });

                if (searchPlacemarkRef.current) {
                    searchPlacemarkRef.current.geometry.setCoordinates(coords);
                    searchPlacemarkRef.current.properties.set({ hintContent: q });
                } else {
                    const pm = new window.ymaps.Placemark(
                        coords,
                        { hintContent: q },
                        { preset: "islands#violetDotIcon", zIndex: 9999, zIndexActive: 10099 }
                    );
                    searchPlacemarkRef.current = pm;
                    mapInstanceRef.current.geoObjects.add(pm);
                }
            } catch (e) {
                console.error("Ошибка поиска:", e);
            }
        },
        [searchCity]
    );

    // ============================
    // 4) Точки / ТС / кастомные метки
    // ============================
    const updatePlacemarks = useCallback(() => {
        if (!layersRef.current || !window.ymaps) return;

        const { basePoints, tsPoints, customMarks: customLayer } = layersRef.current;

        const baseVisible = layersConfig?.basePoints?.visible !== false;
        const tsVisible = layersConfig?.tsPoints?.visible !== false;
        const customVisible = layersConfig?.customMarks?.visible !== false;

        basePoints.removeAll();
        //tsPoints.removeAll();
        customLayer.removeAll();

        initWarningLayout();

        const baseZ = getZIndex(layersConfig, "basePoints", 10);

        const customZ = getZIndex(layersConfig, "customMarks", 30);

        // Статус КП из видимых planned-маршрутов.
        // 1) по id КП (RoutePoint.id / containerPlatformId)
        // 2) fallback по координате, если id в разных форматах или у точки id не пришел
        const cpCompletedById = new Map<string, boolean | null>();
        const cpCompletedByCoord = new Map<string, boolean | null>();
        const coordKey = (lat: number, lon: number) => `${lat.toFixed(6)}|${lon.toFixed(6)}`;
        routes.forEach((r) => {
            if (!r.plannedMeta) return;
            r.points.forEach((p) => {
                const id = String(p.id ?? "").trim().toLowerCase();
                if (p.completed === true) {
                    if (id) cpCompletedById.set(id, true);
                    cpCompletedByCoord.set(coordKey(p.coords[0], p.coords[1]), true);
                }
                else if (p.completed === false) {
                    if (id && !cpCompletedById.has(id)) cpCompletedById.set(id, false);
                    const k = coordKey(p.coords[0], p.coords[1]);
                    if (!cpCompletedByCoord.has(k)) cpCompletedByCoord.set(k, false);
                } else if (p.completed === null) {
                    if (id && !cpCompletedById.has(id)) cpCompletedById.set(id, null);
                    const k = coordKey(p.coords[0], p.coords[1]);
                    if (!cpCompletedByCoord.has(k)) cpCompletedByCoord.set(k, null);
                }
            });
        });

        if (baseVisible) {
            containerPlatforms?.forEach((p) => {
                const cpId = String(p.id ?? "").trim().toLowerCase();
                const cpCoords = mapStringToCoordinate(p.centerPoint);
                const completed =
                    cpCompletedById.get(cpId) ??
                    cpCompletedByCoord.get(coordKey(cpCoords[0], cpCoords[1]));

                const statusColor: PointColor | null =
                    completed === true ? "green" : completed === false ? "red" : completed === null ? "orange" : null;
                const effectiveColor: PointColor = statusColor ?? "green";

                if (criticalActive && effectiveColor !== "red") return;

                const preset =
                    statusColor == null
                        ? "islands#blueCircleIcon"
                        : effectiveColor === "green"
                            ? "islands#greenCircleIcon"
                            : effectiveColor === "orange"
                                ? "islands#orangeCircleIcon"
                                : "islands#redCircleIcon";

                basePoints.add(
                    new window.ymaps.Placemark(
                        mapStringToCoordinate(p.centerPoint),
                        { hintContent: `${p.name}`, cPlatformId: p.id, },
                        { preset, zIndex: baseZ, zIndexActive: baseZ + 100 }
                    )
                );

            });
            basePoints.events.add("click", (e) => {
               const target = e.get("target");
               const id = target.properties.get("cPlatformId");
               setOpenContainerPlatforms({isOpen: true, id: id})
            });
        }

        if (!tsVisible) {
            // если слой скрыли — уберём метки из коллекции, но кэш оставим
            for (const pm of tsPlacemarkByIdRef.current.values()) {
                try { tsPoints.remove(pm); } catch {}
            }
            return;
        }

        const tsZ = getZIndex(layersConfig, "tsPoints", 20);

// кого нужно оставить
        const alive = new Set<string>();

        visibleVehicles.forEach((v, idx) => {
            alive.add(v.id);
            console.log('v.hintContent', v.hintContent)
            console.log('v.name', v.name)
            const hint = v.hintContent?.trim() || v.name?.trim() || `ТС ${idx + 1}`;
            const title = v.name?.trim() || hint;

            const existing = tsPlacemarkByIdRef.current.get(v.id);

            const iconOptions = {
                iconLayout: "default#image",
                iconImageHref: getCarIconSvg(),
                iconImageSize: [32, 32],
                iconImageOffset: [-16, -16],
                zIndex: tsZ,
                zIndexActive: tsZ + 100,
            };

            const props = {
                hintContent: hint,
                balloonContent: title,
                iconCaption: title,
            };

            if (existing) {
                try {
                    existing.geometry.setCoordinates(v.coords);
                    existing.properties.set(props);
                } catch {}
            } else {
                const pm = new window.ymaps.Placemark(v.coords, props, iconOptions);
                tsPlacemarkByIdRef.current.set(v.id, pm);
                tsPoints.add(pm);
            }
        });

// удалить те, кого больше нет в visibleVehicles
        for (const [id, pm] of tsPlacemarkByIdRef.current.entries()) {
            if (!alive.has(id)) {
                try { tsPoints.remove(pm); } catch {}
                tsPlacemarkByIdRef.current.delete(id);
            }
        }

        if (customVisible) {
            customMarks.forEach((m) => {
                if (criticalActive && m.color !== "red") return;

                const opts = warningLayoutRef.current
                    ? {
                        iconLayout: warningLayoutRef.current,
                        iconImageSize: [32, 32],
                        iconImageOffset: [-16, -16],
                        zIndex: customZ,
                        zIndexActive: customZ + 100,
                    }
                    : { preset: "islands#redDotIcon", zIndex: customZ, zIndexActive: customZ + 100 };

                customLayer.add(new window.ymaps.Placemark(m.coords, { hintContent: m.text }, opts));
            });
        }
    }, [layersConfig, initWarningLayout, routes, visibleVehicles, containerPlatforms, criticalActive, setOpenContainerPlatforms, customMarks]);

    // ============================
    // 5) Геозоны (объединено: restrictedZones + drawnShapes)
    // ============================
    const updateGeozones = useCallback(() => {
        if (!layersRef.current || !window.ymaps) return;

        const { restrictedZones: zonesLayer } = layersRef.current;
        const zonesVisible = layersConfig?.restrictedZones?.visible !== false;

        zonesLayer.removeAll();
        geofencePolygonByIdRef.current.clear();

        if (!zonesVisible) return;

        const zonesZ = getZIndex(layersConfig, "restrictedZones", 40);

        visibleZones.forEach((z) => {
            const commonOpts = {
                strokeColor: z.style?.strokeColor ?? "#0E8F4B",
                strokeWidth: z.style?.strokeWidth ?? 3,
                strokeOpacity: z.style?.strokeOpacity ?? 0.9,
                fillColor: z.style?.fillColor ?? "#0E8F4B",
                fillOpacity: z.style?.fillOpacity ?? 0.12,
                zIndex: z.style?.zIndex ?? zonesZ,
            };

            const poly = new window.ymaps.Polygon(
                z.coordinates,
                {
                    hintContent: z.name,
                    geofenceId: z.id,
                },
                commonOpts
            );

            geofencePolygonByIdRef.current.set(String(z.id), poly);
            zonesLayer.add(poly);
        });
        zonesLayer.events.remove("click");
        zonesLayer.events.add("click", (e: any) => {
            const target = e.get("target");
            const id = target?.properties?.get?.("geofenceId");
            if (!id) return;
            setOpenGeofenceModal?.({ isOpen: true, id: String(id) });
        });
    }, [visibleZones, layersConfig, setOpenGeofenceModal]);
    const startEditGeofenceContour = useCallback((geofenceId: string) => {
        const poly = geofencePolygonByIdRef.current.get(String(geofenceId));
        if (!poly) return;

        if (activeEditObjRef.current && activeEditObjRef.current !== poly) {
            try { activeEditObjRef.current.editor?.stopEditing?.(); } catch {}
        }

        activeEditObjRef.current = poly;
        setDrawMode("drawing");
        setActiveDrawType("polygon");
        try { poly.editor?.startEditing?.(); } catch {}
    }, []);

    // ============================
    // 6) Рисование НОВЫХ полигонов
    // ============================
    const startDrawingPolygon = useCallback(() => {
        if (!layersRef.current || !window.ymaps) return;

        const visible = layersConfig?.drawnShapes?.visible !== false;
        if (!visible) return;

        const { drawnShapes: layer } = layersRef.current;

        const tempObj = new window.ymaps.Polygon([[]], {}, {});
        layer.add(tempObj);

        activeShapeObjRef.current = tempObj;
        setDrawMode("drawing");
        setActiveDrawType("polygon");

        try {
            tempObj.editor?.startDrawing?.();
        } catch (e) {
            console.warn("Не удалось запустить режим рисования:", e);
        }
    }, [layersConfig]);

    const finishDrawing = useCallback(async () => {
        if (activeEditObjRef.current) {
            try { activeEditObjRef.current.editor?.stopEditing?.(); } catch {}
            activeEditObjRef.current = null;
            setDrawMode("idle");
            setActiveDrawType(null);

            try {
                onFinishEditContour?.();
            } catch {}

            return;
        }

        const obj = activeShapeObjRef.current;
        if (!obj) return;

        const coords = obj.geometry?.getCoordinates?.();

        try {
            obj.editor?.stopDrawing?.();
        } catch {}

        if (!coords || !Array.isArray(coords[0]) || coords[0].length < 3) {
            try {
                layersRef.current?.drawnShapes?.remove(obj);
            } catch {}
            setDrawMode("idle");
            setActiveDrawType(null);
            activeShapeObjRef.current = null;
            return;
        }

        const seq = getCoordinatesSequenceFromCoords("polygon", coords);

        if (onRequestShapeMeta) {
            const res = await onRequestShapeMeta({ type: "polygon", coordinates: seq });
            if (!res) {
                try {
                    layersRef.current?.drawnShapes?.remove(obj);
                } catch {}
                setDrawMode("idle");
                setActiveDrawType(null);
                activeShapeObjRef.current = null;
                return;
            }
            onGeofenceCreated?.();
        }
        try {
            layersRef.current?.drawnShapes?.remove(obj);
        } catch {}

        setDrawMode("idle");
        setActiveDrawType(null);
        activeShapeObjRef.current = null;
    }, [onFinishEditContour, onRequestShapeMeta, onGeofenceCreated]);

    const cancelDrawing = useCallback(() => {
        if (activeEditObjRef.current) {
            try { activeEditObjRef.current.editor?.stopEditing?.(); } catch {}
            activeEditObjRef.current = null;
            setDrawMode("idle");
            setActiveDrawType(null);
            return;
        }
        const obj = activeShapeObjRef.current;
        if (!obj) return;

        try {
            obj.editor?.stopDrawing?.();
        } catch {}

        try {
            layersRef.current?.drawnShapes?.remove(obj);
        } catch {}

        setDrawMode("idle");
        setActiveDrawType(null);
        activeShapeObjRef.current = null;
    }, []);

    // ============================
    // 7) Маршруты
    // ============================
    const makeSignature = (coords: Array<[number, number]>) => JSON.stringify(coords);

    const applyRouteStyle = useCallback((multiRoute: any, isActive: boolean, style: any, baseZ: number) => {
        const widthBase = style?.strokeWidth ?? 8;
        const widthInactive = Math.max(4, widthBase - 3);
        const opBase = style?.strokeOpacity ?? 0.95;
        const opInactive = 0.58;

        multiRoute.options.set({
            boundsAutoApply: false,
            wayPointVisible: false,
            viaPointVisible: false,
            routeActiveStrokeWidth: isActive ? widthBase : widthInactive,
            routeActiveStrokeOpacity: isActive ? opBase : opInactive,
            routeActiveStrokeColor: style?.strokeColor ?? "#1e98ff",
            routeActiveStrokeStyle: style?.strokeStyle ?? "solid",
            zIndex: (style?.zIndex ?? baseZ) + (isActive ? 200 : 0),
        });
    }, []);

    const updateRoutes = useCallback(() => {
        if (!layersRef.current || !window.ymaps) return;

        const { routes: routesLayer, routePoints, routeLabels } = layersRef.current;

        const routesLayerVisible = layersConfig?.routes?.visible !== false;
        const pointsVisible = layersConfig?.routePoints?.visible !== false;
        const labelsVisible = layersConfig?.routeLabels?.visible !== false;

        routesLayer.removeAll();
        routePoints.removeAll();
        routeLabels.removeAll();

        if (!routesLayerVisible && !pointsVisible && !labelsVisible) return;

        const routesZ = getZIndex(layersConfig, "routes", 100);
        const routePointsZ = getZIndex(layersConfig, "routePoints", 110);
        const routeLabelsZ = getZIndex(layersConfig, "routeLabels", 120);

        const activeSet = new Set(activeRouteIds);

        const validRoutes = routes.filter((r) => {
            if (r.visible === false) return false;
            if (!RouteService.validateRoute(r)) return false;
            const coords = RouteService.getRouteCoordinates(r);
            return coords.length >= 2;
        });

        const orderedToDraw = [
            ...validRoutes.filter((r) => !activeSet.has(r.id)),
            ...validRoutes.filter((r) => activeSet.has(r.id)),
        ];

        orderedToDraw.forEach((route, index) => {
            const coords = RouteService.getRouteCoordinates(route);
            const signature = makeSignature(coords);
            const cached = routeCacheRef.current.get(route.id);
            const isActive = activeSet.has(route.id);
            const style = route.style ?? RouteService.getStyleByIndex(index);

            const addPolylineFallback = () => {
                const poly = new window.ymaps.Polyline(
                    coords,
                    { hintContent: route.name },
                    {
                        strokeColor: style.strokeColor ?? "#1e98ff",
                        strokeWidth: isActive
                            ? (style.strokeWidth ?? 8)
                            : Math.max(4, (style.strokeWidth ?? 8) - 3),
                        strokeOpacity: isActive ? (style.strokeOpacity ?? 0.95) : 0.58,
                        strokeStyle: style.strokeStyle ?? "solid",
                        zIndex: (style.zIndex ?? routesZ) + (isActive ? 200 : 0),
                    }
                );
                routesLayer.add(poly);
            };

            let multiRoute = cached?.obj;

            if (!cached || cached.signature !== signature) {
                try {
                    multiRoute = new window.ymaps.multiRouter.MultiRoute(
                        {
                            referencePoints: coords,
                            params: { routingMode: "auto", results: 1 },
                        },
                        {}
                    );

                    multiRoute.model.events.add("requestsuccess", () => {
                        const active = multiRoute.getActiveRoute?.();
                        if (!active) {
                            routesLayer.remove(multiRoute);
                            addPolylineFallback();
                        }
                    });

                    multiRoute.model.events.add("requestfail", () => {
                        try {
                            routesLayer.remove(multiRoute);
                        } catch {}
                        addPolylineFallback();
                    });

                    routeCacheRef.current.set(route.id, { obj: multiRoute, signature });

                    if (!centeredOnceRef.current && isActive) {
                        multiRoute.model.events.add("requestsuccess", () => {
                            if (centeredOnceRef.current) return;
                            centeredOnceRef.current = true;
                            const bounds = multiRoute.getBounds?.();
                            if (bounds && mapInstanceRef.current) {
                                mapInstanceRef.current.setBounds(bounds, {
                                    checkZoomRange: true,
                                    zoomMargin: 40,
                                });
                            }
                        });
                    }
                } catch (err) {
                    multiRoute = null;
                }
            }

            if (routesLayerVisible) {
                if (multiRoute) {
                    applyRouteStyle(multiRoute, isActive, style, routesZ);
                    routesLayer.add(multiRoute);
                } else {
                    addPolylineFallback();
                }
            }

            if (pointsVisible) {
                const orderedPoints = [...route.points].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                orderedPoints.forEach((p, i) => {
                    const isStart = i === 0;
                    const isEnd = i === orderedPoints.length - 1;
                    const iconContent = isStart ? "A" : isEnd ? "B" : String(i + 1);
                    const hint = isStart
                        ? `Старт: ${p.name || "КП 1"}`
                        : isEnd
                            ? `Финиш: ${p.name || `КП ${i + 1}`}`
                            : `${p.name || `КП ${i + 1}`}`;
                    const preset =
                        p.completed === true
                            ? "islands#greenStretchyIcon"
                            : p.completed === false
                                ? "islands#redStretchyIcon"
                                : p.completed === null
                                    ? "islands#orangeStretchyIcon"
                                    : isStart
                                        ? "islands#greenStretchyIcon"
                                        : isEnd
                                            ? "islands#redStretchyIcon"
                                            : "islands#blueStretchyIcon";

                    routePoints.add(
                        new window.ymaps.Placemark(
                            p.coords,
                            { iconContent, hintContent: hint },
                            {
                                preset,
                                zIndex: routePointsZ + (isStart || isEnd ? 50 : 0),
                                zIndexActive: routePointsZ + 200,
                            }
                        )
                    );
                });
            }

            if (labelsVisible && route.points.length) {
                const orderedPoints = [...route.points].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                const start = orderedPoints[0];

                routeLabels.add(
                    new window.ymaps.Placemark(
                        start.coords,
                        { iconContent: route.name },
                        {
                            preset: "islands#darkBlueStretchyIcon",
                            zIndex: routeLabelsZ,
                            zIndexActive: routeLabelsZ + 100,
                        }
                    )
                );
            }
        });

        const aliveIds = new Set(routes.map((r) => r.id));
        for (const id of routeCacheRef.current.keys()) {
            if (!aliveIds.has(id)) routeCacheRef.current.delete(id);
        }
    }, [routes, layersConfig, activeRouteIds, applyRouteStyle]);

    // ============================
    // 8) Эффекты отрисовки
    // ============================
    useEffect(() => {
        if (!mapReady) return;
        updatePlacemarks();
    }, [mapReady, updatePlacemarks]);

    useEffect(() => {
        if (!mapReady) return;
        updateGeozones();
    }, [mapReady, updateGeozones]);

    useEffect(() => {
        if (!mapReady) return;
        updateRoutes();
    }, [mapReady, updateRoutes]);

    return {
        mapRef,
        searchByAddress,
        drawMode,
        activeDrawType,
        startDrawingPolygon,
        finishDrawingOrEditing: finishDrawing,
        cancelDrawingOrEditing: cancelDrawing,
        getGeofencePointsFromMap: (id: string) => {
            const poly = geofencePolygonByIdRef.current.get(String(id));
            return poly ? getCoordinatesSequenceFromPolygon(poly) : [];
        },
        startEditGeofenceContour,
    };
}